import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinanceReports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingInvoicePdf, setDownloadingInvoicePdf] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    athlete_id: '',
    status: ''
  });

  useEffect(() => {
    fetchAthletes();
    fetchReport();
  }, []);

  const fetchAthletes = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      setAthletes(response.data);
    } catch (error) {
      console.error('Failed to fetch athletes:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.athlete_id) params.append('athlete_id', filters.athlete_id);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(`${API}/finance/reports?${params.toString()}`);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    fetchReport();
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      athlete_id: '',
      status: ''
    });
    setTimeout(() => fetchReport(), 100);
  };

  const downloadReportPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.athlete_id) params.append('athlete_id', filters.athlete_id);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(`${API}/finance/reports/pdf?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Finance_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    setDownloadingInvoicePdf(invoiceId);
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Invoice PDF downloaded');
    } catch (error) {
      toast.error('Failed to download invoice PDF');
    } finally {
      setDownloadingInvoicePdf(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getStatusBadge = (status) => {
    const colors = {
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      overdue: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || colors.pending;
  };

  const canEdit = user?.role === 'Admin' || user?.role === 'Staff';

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Finance Reports
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">View and export financial data</p>
          </div>
          <div className="flex gap-2">
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => navigate('/finance')}
                data-testid="back-to-finance"
              >
                ← Back to Finance
              </Button>
            )}
            <Button
              onClick={downloadReportPdf}
              disabled={downloadingPdf || !reportData?.invoices?.length}
              className="bg-pfap-green hover:bg-pfap-green/90"
              data-testid="download-report-pdf"
            >
              {downloadingPdf ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Generating...
                </>
              ) : (
                <>📥 Export Report PDF</>
              )}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6" data-testid="filters-card">
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
            <CardDescription>Filter invoices by date range, athlete, or status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  data-testid="filter-start-date"
                />
              </div>
              <div>
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  data-testid="filter-end-date"
                />
              </div>
              <div>
                <Label htmlFor="athlete">Athlete</Label>
                <Select
                  value={filters.athlete_id || 'all'}
                  onValueChange={(value) => handleFilterChange('athlete_id', value === 'all' ? '' : value)}
                >
                  <SelectTrigger data-testid="filter-athlete">
                    <SelectValue placeholder="All Athletes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Athletes</SelectItem>
                    {athletes.map((athlete) => (
                      <SelectItem key={athlete.id} value={athlete.id}>
                        {athlete.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={filters.status || 'all'}
                  onValueChange={(value) => handleFilterChange('status', value === 'all' ? '' : value)}
                >
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={applyFilters} data-testid="apply-filters">
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={clearFilters} data-testid="clear-filters">
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {reportData?.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="summary-cards">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <p className="text-sm text-blue-700 font-medium">Total Invoiced</p>
                <p className="text-2xl font-bold text-blue-900" data-testid="summary-total-invoiced">
                  {formatCurrency(reportData.summary.total_invoiced)}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {reportData.summary.invoice_count} invoice(s)
                </p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6">
                <p className="text-sm text-emerald-700 font-medium">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-900" data-testid="summary-total-paid">
                  {formatCurrency(reportData.summary.total_paid)}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  {reportData.summary.paid_count} paid
                </p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6">
                <p className="text-sm text-amber-700 font-medium">Pending</p>
                <p className="text-2xl font-bold text-amber-900" data-testid="summary-pending">
                  {formatCurrency(reportData.summary.total_pending)}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {reportData.summary.pending_count} pending
                </p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6">
                <p className="text-sm text-red-700 font-medium">Outstanding</p>
                <p className="text-2xl font-bold text-red-900" data-testid="summary-outstanding">
                  {formatCurrency(reportData.summary.outstanding_balance)}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {reportData.summary.overdue_count} overdue
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Invoice Table */}
        <Card data-testid="invoices-table-card">
          <CardHeader>
            <CardTitle>Invoices</CardTitle>
            <CardDescription>
              {reportData?.invoices?.length || 0} invoice(s) found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : reportData?.invoices?.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-500">
                <p className="text-lg mb-2">No invoices found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="invoices-table">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                      <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Invoice #</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Athlete</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Description</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Amount</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Status</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Due Date</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData?.invoices?.map((invoice) => (
                      <tr
                        key={invoice.id}
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:bg-gray-900"
                        data-testid={`invoice-row-${invoice.id}`}
                      >
                        <td className="py-3 px-4 font-medium">{invoice.invoice_number}</td>
                        <td className="py-3 px-4">{invoice.athlete_name}</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-500 max-w-xs truncate">
                          {invoice.description}
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          {formatCurrency(invoice.amount)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={getStatusBadge(invoice.status)}>
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center text-gray-600 dark:text-gray-500">
                          {new Date(invoice.due_date).toLocaleDateString('pt-PT')}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadInvoicePdf(invoice.id, invoice.invoice_number)}
                            disabled={downloadingInvoicePdf === invoice.id}
                            data-testid={`download-invoice-${invoice.id}`}
                          >
                            {downloadingInvoicePdf === invoice.id ? '...' : '📄 PDF'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default FinanceReports;
